import { 
  BookOpen,
  Video,
  Play,
  Sparkles,
  Trophy,
  Star,
  Lightbulb,
  Target,
  Rocket,
  Brain,
  Bot,
  TrendingUp,
  FileCheck,
  Upload,
  CheckCircle2,
  Award,
  Calendar,
  Clock,
  Users,
  MessageSquare,
  MessagesSquare,
  HelpCircle,
  Layers,
  Zap,
  BarChart3,
  PieChart,
  Activity
} from 'lucide-react';
import type { OnboardingStep, OnboardingBenefit } from '@/components/ModuleOnboarding';

// Content Module Onboarding
export const contentOnboardingSteps: OnboardingStep[] = [
  {
    id: 1,
    icon: Video,
    title: '¡Bienvenido al Centro de Contenido!',
    description: 'Aquí encontrarás todo el material de aprendizaje personalizado según tu nivel de diagnóstico.',
    highlight: 'Contenido adaptado a ti',
    color: 'from-primary to-primary/60'
  },
  {
    id: 2,
    icon: BookOpen,
    title: 'Capítulos y Lecciones',
    description: 'El contenido está organizado en capítulos temáticos. Cada uno contiene videos, lecturas y ejercicios prácticos.',
    highlight: 'Aprende a tu ritmo',
    color: 'from-accent to-accent/60'
  },
  {
    id: 3,
    icon: Play,
    title: 'Vista Previa Interactiva',
    description: 'Pasa el cursor sobre las lecciones de video para ver una vista previa animada antes de comenzar.',
    highlight: 'Explora antes de elegir',
    color: 'from-success to-success/60'
  },
  {
    id: 4,
    icon: Sparkles,
    title: '¡Comienza tu Aprendizaje!',
    description: 'Desbloquea capítulos completando lecciones. Tu progreso se guarda automáticamente.',
    highlight: 'Progreso continuo',
    color: 'from-warning to-warning/60'
  }
];

export const contentOnboardingBenefits: OnboardingBenefit[] = [
  { icon: Trophy, text: 'Insignias por completar capítulos' },
  { icon: Star, text: 'Contenido desbloqueado progresivamente' },
  { icon: BarChart3, text: 'Seguimiento de tu avance' }
];

// Challenge Lab Onboarding
export const challengeLabOnboardingSteps: OnboardingStep[] = [
  {
    id: 1,
    icon: Lightbulb,
    title: '¡Bienvenido al Laboratorio de Reto y Soluciones!',
    description: 'Este es el espacio donde definirás el proyecto que desarrollarás durante todo la ruta.',
    highlight: 'Tu proyecto personal',
    color: 'from-primary to-primary/60'
  },
  {
    id: 2,
    icon: Bot,
    title: 'Asistente de IA',
    description: 'Conversa con nuestro asistente inteligente para identificar el reto que mejor se adapta a tu contexto laboral.',
    highlight: 'Guía personalizada',
    color: 'from-accent to-accent/60'
  },
  {
    id: 3,
    icon: Brain,
    title: 'Tipos de Solución',
    description: 'Elige entre 5 tipos de soluciones: Contenido, Datos, Asistentes, Automatizaciones o Coding.',
    highlight: '5 caminos posibles',
    color: 'from-success to-success/60'
  },
  {
    id: 4,
    icon: Rocket,
    title: '¡Define tu Reto!',
    description: 'Al finalizar tendrás claro qué problema resolver y qué tipo de solución construirás.',
    highlight: 'Listo para ejecutar',
    color: 'from-warning to-warning/60'
  }
];

export const challengeLabOnboardingBenefits: OnboardingBenefit[] = [
  { icon: Target, text: 'Reto definido y acotado' },
  { icon: TrendingUp, text: 'Ruta clara de desarrollo' },
  { icon: Trophy, text: 'Base para tu proyecto final' }
];

// Modules (Soluciones) Onboarding
export const modulesOnboardingSteps: OnboardingStep[] = [
  {
    id: 1,
    icon: Layers,
    title: '¡Bienvenido a los Módulos de Solución!',
    description: 'Aquí seguirás tu progresión a través de 5 tipos de soluciones, desde la más simple hasta la más avanzada.',
    highlight: 'Progresión estructurada',
    color: 'from-primary to-primary/60'
  },
  {
    id: 2,
    icon: Zap,
    title: 'Desbloqueo Secuencial',
    description: 'Completa un tipo de solución para desbloquear el siguiente. Cada uno aumenta en complejidad y valor.',
    highlight: 'Avance progresivo',
    color: 'from-accent to-accent/60'
  },
  {
    id: 3,
    icon: FileCheck,
    title: 'Entregables por Módulo',
    description: 'Cada módulo tiene entregables específicos que debes completar y que serán validados por tu mentor.',
    highlight: 'Validación de aprendizaje',
    color: 'from-success to-success/60'
  },
  {
    id: 4,
    icon: Trophy,
    title: '¡Rumbo al Demoday!',
    description: 'Completar todos los módulos te habilita para presentar tu proyecto en el Demoday final.',
    highlight: 'Meta: Demoday',
    color: 'from-warning to-warning/60'
  }
];

export const modulesOnboardingBenefits: OnboardingBenefit[] = [
  { icon: Award, text: 'Badges por cada módulo completado' },
  { icon: CheckCircle2, text: 'Validación de mentores' },
  { icon: Rocket, text: 'Acceso al Demoday' }
];

// Deliverables Onboarding
export const deliverablesOnboardingSteps: OnboardingStep[] = [
  {
    id: 1,
    icon: FileCheck,
    title: '¡Bienvenido a Entregables!',
    description: 'Este es el centro de control de todos tus entregables de la ruta.',
    highlight: 'Todo en un lugar',
    color: 'from-primary to-primary/60'
  },
  {
    id: 2,
    icon: Upload,
    title: 'Carga tus Trabajos',
    description: 'Sube tus entregables con enlaces, selecciona la herramienta usada y el tipo de solución.',
    highlight: 'Proceso simple',
    color: 'from-accent to-accent/60'
  },
  {
    id: 3,
    icon: CheckCircle2,
    title: 'Validación de Mentores',
    description: 'Tu mentor revisará cada entregable y te dará retroalimentación para mejorar.',
    highlight: 'Feedback personalizado',
    color: 'from-success to-success/60'
  },
  {
    id: 4,
    icon: Award,
    title: '¡Demuestra tu Progreso!',
    description: 'Los entregables validados contribuyen a desbloquear nuevos módulos y alcanzar el Demoday.',
    highlight: 'Avanza con evidencias',
    color: 'from-warning to-warning/60'
  }
];

export const deliverablesOnboardingBenefits: OnboardingBenefit[] = [
  { icon: TrendingUp, text: 'Seguimiento de tu progreso' },
  { icon: MessageSquare, text: 'Retroalimentación de mentores' },
  { icon: Trophy, text: 'Desbloqueo de módulos' }
];

// Calendar Onboarding
export const calendarOnboardingSteps: OnboardingStep[] = [
  {
    id: 1,
    icon: Calendar,
    title: '¡Bienvenido al Calendario!',
    description: 'Aquí encontrarás todos los eventos de la ruta: sesiones en vivo y sesiones de mentoría.',
    highlight: 'Tu agenda de la ruta',
    color: 'from-primary to-primary/60'
  },
  {
    id: 2,
    icon: Users,
    title: 'Sesiones en Vivo Grupales',
    description: 'Sesiones en vivo con expertos donde aprenderás técnicas y herramientas de IA aplicada.',
    highlight: 'Aprendizaje en vivo',
    color: 'from-accent to-accent/60'
  },
  {
    id: 3,
    icon: Clock,
    title: 'Mentorías Individuales',
    description: 'Sesiones 1:1 con tu mentor para revisar tu proyecto y resolver dudas específicas.',
    highlight: 'Atención personalizada',
    color: 'from-success to-success/60'
  },
  {
    id: 4,
    icon: Play,
    title: '¡No te Pierdas Nada!',
    description: 'Accede a las grabaciones de sesiones pasadas y sube entregables directamente desde cada evento.',
    highlight: 'Recursos siempre disponibles',
    color: 'from-warning to-warning/60'
  }
];

export const calendarOnboardingBenefits: OnboardingBenefit[] = [
  { icon: Video, text: 'Grabaciones de sesiones pasadas' },
  { icon: Upload, text: 'Carga de entregables desde eventos' },
  { icon: Users, text: 'Conexión con mentores y compañeros' }
];

// Projects Onboarding
export const projectsOnboardingSteps: OnboardingStep[] = [
  {
    id: 1,
    icon: Target,
    title: '¡Bienvenido a Proyectos!',
    description: 'Este es el centro de gestión de tu proyecto principal de la ruta.',
    highlight: 'Tu proyecto definido',
    color: 'from-primary to-primary/60'
  },
  {
    id: 2,
    icon: FileCheck,
    title: 'Definición del Reto',
    description: 'Aquí encontrarás tu reto empresarial y podrás editar todos los detalles de tu proyecto.',
    highlight: 'Problema + Solución',
    color: 'from-accent to-accent/60'
  },
  {
    id: 3,
    icon: Upload,
    title: 'Carga de Evidencias',
    description: 'Sube enlaces, archivos y demos de tu proyecto con la herramienta utilizada.',
    highlight: 'Documenta tu avance',
    color: 'from-success to-success/60'
  },
  {
    id: 4,
    icon: Rocket,
    title: '¡Genera valor real!',
    description: 'Tu proyecto evoluciona con cada entregable para generar impacto y resultados medibles en tu organización.',
    highlight: 'Meta: Valor e impacto real',
    color: 'from-warning to-warning/60'
  }
];

export const projectsOnboardingBenefits: OnboardingBenefit[] = [
  { icon: Target, text: 'Reto claramente definido' },
  { icon: TrendingUp, text: 'Seguimiento de progreso' },
  { icon: Trophy, text: 'Impacto medible de tu proyecto' }
];

// Forum Onboarding
export const forumOnboardingSteps: OnboardingStep[] = [
  {
    id: 1,
    icon: MessagesSquare,
    title: '¡Bienvenido a la Comunidad!',
    description: 'Este es el espacio de colaboración de LÍDER IA donde puedes compartir y aprender.',
    highlight: 'Comunidad activa',
    color: 'from-primary to-primary/60'
  },
  {
    id: 2,
    icon: HelpCircle,
    title: 'Pregunta y Aprende',
    description: 'Publica tus dudas, comparte soluciones y aprende de las experiencias de otros participantes.',
    highlight: 'Aprendizaje colaborativo',
    color: 'from-accent to-accent/60'
  },
  {
    id: 3,
    icon: Lightbulb,
    title: 'Comparte tus Proyectos',
    description: 'Muestra tu progreso, recibe feedback y celebra los logros de la comunidad.',
    highlight: 'Visibilidad de tu trabajo',
    color: 'from-success to-success/60'
  },
  {
    id: 4,
    icon: Users,
    title: '¡Conéctate con Otros!',
    description: 'Encuentra mentores, colaboradores y comparte recursos útiles para todos.',
    highlight: 'Networking profesional',
    color: 'from-warning to-warning/60'
  }
];

export const forumOnboardingBenefits: OnboardingBenefit[] = [
  { icon: MessageSquare, text: 'Respuestas de la comunidad' },
  { icon: Star, text: 'Reconocimiento por participación' },
  { icon: TrendingUp, text: 'Crecimiento profesional' }
];

// Metrics Onboarding
export const metricsOnboardingSteps: OnboardingStep[] = [
  {
    id: 1,
    icon: BarChart3,
    title: '¡Bienvenido a tus Métricas!',
    description: 'Este es tu dashboard personal con todo tu progreso en la ruta.',
    highlight: 'Tu progreso en un vistazo',
    color: 'from-primary to-primary/60'
  },
  {
    id: 2,
    icon: PieChart,
    title: 'Visualización de Avance',
    description: 'Mira tu progreso general, entregables validados, módulos completados y racha de días activos.',
    highlight: 'Métricas en tiempo real',
    color: 'from-accent to-accent/60'
  },
  {
    id: 3,
    icon: Activity,
    title: 'Mapa de Habilidades',
    description: 'Un radar visual muestra tu dominio en cada tipo de solución: Contenido, Datos, Asistentes, Automatización y Coding.',
    highlight: 'Fortalezas y áreas de mejora',
    color: 'from-success to-success/60'
  },
  {
    id: 4,
    icon: Trophy,
    title: '¡Tus Insignias y Logros!',
    description: 'Visualiza las insignias que has ganado y las que te faltan por desbloquear.',
    highlight: 'Celebra tu progreso',
    color: 'from-warning to-warning/60'
  }
];

export const metricsOnboardingBenefits: OnboardingBenefit[] = [
  { icon: TrendingUp, text: 'Visualiza tu avance' },
  { icon: Award, text: 'Rastrea tus insignias' },
  { icon: Target, text: 'Identifica próximos pasos' }
];
